import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import ClinicMark from "@/components/ClinicMark";
import { Heart, Loader2 } from "lucide-react";

export default function PatientLogin() {
  const { login, error, user } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "patient") navigate("/portal");
    else if (user) navigate("/");
  }, [user, navigate]);

  const clinicName = clinic?.name || "Your Clinic";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/portal");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center overflow-hidden mb-4">
            <ClinicMark className="text-xl" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="patient-login-clinic-name">{clinicName}</h1>
          <p className="text-sm text-muted-foreground mt-1">{clinic?.welcome_message || "Sign in to your patient portal."}</p>
        </div>

        <form onSubmit={submit} className="space-y-4 bg-card border border-border rounded-xl p-6">
          <div>
            <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Email</label>
            <input
              data-testid="patient-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder="you@email.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Password</label>
            <input
              data-testid="patient-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="patient-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
          )}

          <button
            data-testid="patient-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>

          <p className="text-xs text-center text-muted-foreground pt-1">Demo: james.sullivan@example.com / Patient123!</p>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Heart className="w-3 h-3 inline mr-1 text-rose-400" />
          Clinic staff? <Link to="/login" className="font-medium text-foreground hover:underline" data-testid="goto-staff-login">Staff sign in</Link>
        </p>
      </div>
    </div>
  );
}
